import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PaginatedUncreditedReservationsReportList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { urlParamsFromUrl } from 'utils/functions';

interface QueryKeyType {
  pageParam?: UrlParams;
}

interface UseUncreditedReservations {
  startDate: string;
  endDate: string;
  search?: string;
  eventTypeIds?: string[];
  limit?: string;
}

export const UNCREDITED_RESERVATIONS_QUERY_KEY = 'uncredited-reservations';

function useUncreditedReservations({
  startDate,
  endDate,
  search,
  eventTypeIds,
  limit = '20',
}: UseUncreditedReservations): UseInfiniteQueryResult<
  AxiosResponse<PaginatedUncreditedReservationsReportList>
> {
  const fetchUncreditedReservations = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedUncreditedReservationsReportList>> => {
      const query = new URLSearchParams({ ...pageParam });

      // Required parameters from serializer
      query.append('start_date', startDate);
      query.append('end_date', endDate);

      // Optional parameters
      if (search) {
        query.append('search', search);
      }
      if (eventTypeIds && eventTypeIds.length > 0) {
        eventTypeIds.forEach(id => query.append('event_type_ids', id));
      }

      return client.get(`/reports/reservations/uncredited-reservations/`, { params: query });
    },
    [search, startDate, endDate, eventTypeIds, limit],
  );

  return useInfiniteQuery(
    [UNCREDITED_RESERVATIONS_QUERY_KEY, search, startDate, endDate, eventTypeIds],
    fetchUncreditedReservations,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
    },
  );
}

export default useUncreditedReservations;
